import { useIsSmOrSmaller } from '@dreamstack/feature-components/src/hooks/useWindowSize'
import { HeadingH3 } from '@dreamstack/feature-components/src/layout/Texts'
import { useTranslation } from '@dreamstack/i18n'
import max from 'lodash/max'
import maxBy from 'lodash/maxBy'
import dynamic from 'next/dynamic'
import type { FunctionComponent } from 'react'
import { GraphLegends } from '../HomeOwnershipReport/GraphLegends'
import { formatYears } from '../HomeOwnershipReport/Settings'
import { useGraphFontsizeCb } from '../HomeOwnershipReport/useGraphFontsize'
import { RegionLineLayerForBarGraph } from './RegionLineLayerForBarGraph'
import {
  REGION_BAR_GRAPH_BARS,
  REGION_BAR_GRAPH_LABEL,
  REGION_BAR_GRAPH_LEGENDS,
} from './libs/colors'
import { useDataForRegionBarGraph } from './libs/useDataForRegionBarGraph'

const ResponsiveBar = dynamic(
  () => import('@nivo/bar').then((mod) => mod.ResponsiveBar),
  {
    ssr: false,
  }
)

export interface RegionBarGraphProps {
  customProps: RegionBarGraphCustomProps
}

export type RegionBarGraphCustomProps = {
  region: string
}

export const RegionBarGraph: FunctionComponent<
  React.PropsWithChildren<RegionBarGraphProps>
> = ({ customProps }) => {
  const t = useTranslation()
  const region = customProps.region

  const purchaseName = t(
    'accentro:districtLandingPageGraphs.regionAverageGraph.saleLabel'
  )
  const rentalName = t(
    'accentro:districtLandingPageGraphs.regionAverageGraph.rentalLabel'
  )

  // 10.000 € wenn jemand eine bessere Lösung findet
  const data = useDataForRegionBarGraph(region).map((item) => {
    const purchase = item.Kauf
    const rental = item.Miete

    const newData: any = { ...item }
    delete newData.Kauf
    delete newData.Miete

    newData[rentalName] = rental
    newData[purchaseName] = purchase
    return newData
  })

  const graphFontSize = useGraphFontsizeCb()
  const isMobile = useIsSmOrSmaller()

  const maxValueKauf = maxBy(data, (d) => d[purchaseName])[purchaseName]
  const maxValueMiete = maxBy(data, (d) => d[rentalName])[rentalName]
  const maxValue = max([maxValueKauf, maxValueMiete])

  const { lineLayer } = RegionLineLayerForBarGraph(data.length)
  if (region) {
    return (
      //TODO: Localize Text
      <div className="col-span-full bg-accentroAqua-100 h-auto lg:col-start-3 lg:col-span-8">
        <div className="ml-px-40 pt-px-32">
          <HeadingH3 className="mb-px-48">
            {t(
              'accentro:districtLandingPageGraphs.regionBarGraph.offerOfProperties'
            )}{' '}
            {region}
          </HeadingH3>
        </div>
        <div className="flex justify-center">
          <GraphLegends
            identifiers={[
              {
                legend: t(
                  'accentro:districtLandingPageGraphs.regionAverageGraph.rentalOfferts'
                ),
                color: REGION_BAR_GRAPH_LEGENDS[0],
              },
              {
                legend: t(
                  'accentro:districtLandingPageGraphs.regionAverageGraph.saleOffers'
                ),
                color: REGION_BAR_GRAPH_LEGENDS[1],
              },
            ]}
          />
        </div>
        <div className="h-px-576 overflow-hidden  ">
          <ResponsiveBar
            enableLabel={true}
            labelTextColor={REGION_BAR_GRAPH_LABEL}
            labelSkipWidth={25}
            theme={{
              text: {
                fontSize: graphFontSize(),
                fontFamily: 'Montserrat',
              },
              axis: {
                legend: {
                  text: {
                    fontSize: '20px',
                    fontWeight: 500,
                  },
                },
              },
            }}
            groupMode={'grouped'}
            colors={REGION_BAR_GRAPH_BARS}
            keys={[rentalName, purchaseName]}
            indexBy={'id'}
            data={data}
            layers={['grid', 'axes', 'bars', 'markers', 'legends', lineLayer]}
            padding={0.3}
            innerPadding={4}
            valueScale={{ type: 'linear', max: maxValue }}
            indexScale={{ type: 'band', round: true }}
            margin={{ top: 20, bottom: 114, left: 80, right: 80 }}
            enableGridX={false}
            axisTop={null}
            axisBottom={{
              tickSize: 8,
              tickPadding: 12,
              tickRotation: 0,
              legend: t(
                'accentro:districtLandingPageGraphs.regionBarGraph.year'
              ),
              legendOffset: 60,
              legendPosition: 'middle',
              format: (value) => {
                if (isMobile) return formatYears(value)
              },
            }}
            axisLeft={{
              tickSize: 8,
              tickPadding: 12,
              tickRotation: 0,
              legendOffset: 0,
              legendPosition: 'middle',
            }}
          />
        </div>
      </div>
    );
  } else
    return (
      <>
        {t(
          'accentro:districtLandingPageGraphs.regionPieChart.mustSelectRegion'
        )}
      </>
    )
}
